/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AdminDollarInput2,
  AdminInput,
  AdminSelect,
  BTCategoryResponse,
  BTMenuItemResponse,
  BTMenuResponse,
  BTModifierGroupItemResponse,
  Button,
  Div,
  H5,
  Icon,
  Modal,
  PlusIcon,
  PropsModal,
  TextWithTooltipIcon,
  lightTheme,
} from '@vgrubs/components';
import {
  Availability,
  MenuPriceCustomizationV2Response,
} from 'components/src/types/menu.types';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { ImageInput } from '../../components/ImageInput';
import { AddCustomPrice } from './AddCustomPrice';
import { DeleteCustomPriceModal } from './DeleteCustomPriceModal';
import { DeleteImageModal } from './DeleteImageModal';
import { AddModifierGroupComponent } from './ModifierGroupComponents';

export const AddItemModal = ({
  addModal,
  closeModal,
  edit,
  setAddModifierGroupModal,
  menu,
  modifierGroups,
  setModifierGroups,
  handleAddItem,
  handleEditItem,
}: PropsModal & {
  setAddModifierGroupModal: Dispatch<SetStateAction<boolean>>;
  addModal:
    | boolean
    | {
        item: BTMenuItemResponse;
        category: BTCategoryResponse;
      }
    | null;
  menu: BTMenuResponse | null;
  handleAddItem?: (
    image: string | Blob | File | null,
    name: string,
    description: string,
    fromPrice: number,
    toPrice: number,
    categoryId: string,
    modifierGroups: BTModifierGroupItemResponse[] | null,
    preparationNotes: string,
    emptyForm: () => void
  ) => void;
  handleEditItem?: (
    image: string | Blob | File | null,
    name: string,
    description: string,
    fromPrice: number,
    toPrice: number,
    categoryId: string,
    modifierGroups: BTModifierGroupItemResponse[] | null,
    existingItem: BTMenuItemResponse,
    preparationNotes: string,
    emptyForm: () => void,
    removeImage: boolean | null
  ) => void;
  edit?: boolean;
  modifierGroups: BTModifierGroupItemResponse[];
  setModifierGroups: Dispatch<SetStateAction<BTModifierGroupItemResponse[]>>;
}) => {
  const [image, setImage] = useState<string | Blob | File | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [preparationNotes, setPreparationNotes] = useState('');

  const [category, setCategory] = useState('');
  const [fromPrice, setFromPrice] = useState(0);
  const [toPrice, setToPrice] = useState(0);

  const [addCustomPriceModal, setAddCustomPriceModal] = useState(false);
  const [editCustomPriceModal, setEditCustomPriceModal] =
    useState<MenuPriceCustomizationV2Response | null>(null);

  const [customizedPrices, setCustomizedPrices] = useState<
    MenuPriceCustomizationV2Response[]
  >([]);

  const [deleteModal, setDeleteModal] =
    useState<MenuPriceCustomizationV2Response | null>(null);
  const [deleteImageModal, setDeleteImageModal] = useState(false);
  const [removeImage, setRemoveImage] = useState(false);
  const [availability, setAvailability] = useState(Availability.Available);

  const data = addModal;

  const handleValueTotalChangeFromPrice = useCallback((val: number) => {
    setFromPrice(val);
  }, []);

  const handleValueTotalChangeToPrice = useCallback((val: number) => {
    setToPrice(val);
  }, []);

  const typeOptions = (
    <optgroup label="Select Category">
      {menu?.categories?.map((r, i) => (
        <option value={r.id} key={i}>
          {r.name}
        </option>
      ))}
    </optgroup>
  );

  useEffect(() => {
    setCategory(menu?.categories?.[0]?.id.toString() || '');
    if (edit && typeof data === 'object' && data) {
      setName(data?.item?.name || '');
      setDescription(data?.item?.description || '');
      setCategory(
        data?.category?.id.toString() ||
          menu?.categories?.[0]?.id.toString() ||
          ''
      );
      setFromPrice(data?.item?.fromPrice ?? 0);
      setToPrice(data?.item?.toPrice ?? 0);
      setModifierGroups(data?.item?.modifierGroups || []);
      setImage(data?.item?.imageUrl || '');
      setPreparationNotes(data?.item?.preparationNotes || '');
    } else {
      emptyForm();
    }
  }, [data]);

  const emptyForm = () => {
    setImage('');
    setName('');
    setDescription('');
    setCategory(menu?.categories?.[0]?.id.toString() || '');
    setModifierGroups([]);
    setFromPrice(0);
    setToPrice(0);
    setCustomizedPrices([]);
    setRemoveImage(false);
    setPreparationNotes('');
  };

  const handleSubmit = () => {
    if (edit && typeof data === 'object') {
      handleEditItem?.(
        image,
        name,
        description,
        fromPrice,
        toPrice,
        category,
        modifierGroups,
        data?.item as BTMenuItemResponse,
        preparationNotes,
        emptyForm,
        removeImage
      );
    } else {
      handleAddItem?.(
        image,
        name,
        description,
        fromPrice,
        toPrice,
        category,
        modifierGroups,
        preparationNotes,
        emptyForm
      );
    }
  };

  return (
    <>
      <Modal
        isOpen={!!addModal}
        closeModal={closeModal}
        header={edit ? 'Edit Item' : 'Add Item'}
        logo={false}
        footer={
          <Button $selected $w="100%" onClick={handleSubmit}>
            Confirm
          </Button>
        }
        $numOfFloatingButtons={1}
      >
        <Div $flex $center $mb="32">
          <ImageInput
            image={image}
            onChange={(img) => setImage(img)}
            onDelete={() => setDeleteImageModal(true)}
          />
        </Div>
        <Div $flex $column $gap="24px">
          <AdminInput
            placeholder="Enter here"
            label="Item Name"
            value={name}
            onChange={({ target }: { target: HTMLInputElement }) =>
              setName(target.value)
            }
            $textCenter
          />

          <AdminSelect
            value={category}
            options={typeOptions}
            onChange={({ target }: { target: HTMLSelectElement }) => {
              setCategory(target.value);
            }}
            label="Choose Category"
            $textCenter
          />

          <Div $flex $column $gap="4px" $alignCenter>
            <TextWithTooltipIcon
              bold
              text="Suggested Price From - To"
              tooltip="The lower the price the higher the chances of people ordering."
            ></TextWithTooltipIcon>

            <Div $flex $alignCenter $gap="16px" $width="100%">
              <AdminDollarInput2
                $textCenter
                onValueChange={handleValueTotalChangeFromPrice}
                value={fromPrice}
              />
              <AdminDollarInput2
                $textCenter
                onValueChange={handleValueTotalChangeToPrice}
                value={toPrice}
              />
            </Div>
          </Div>

          <AdminInput
            placeholder="Enter here"
            label="Description"
            type="textarea"
            value={description}
            onChange={({ target }: { target: HTMLTextAreaElement }) =>
              setDescription(target.value)
            }
            $textareaHeight="200"
          />

          <AdminInput
            placeholder="Enter here"
            label="Preparation Notes"
            type="textarea"
            value={preparationNotes || ''}
            onChange={({ target }: { target: HTMLTextAreaElement }) =>
              setPreparationNotes(target.value)
            }
            $textareaHeight="200"
          />

          {modifierGroups?.length > 0 ? (
            <>
              <Div $flex $alignCenter $between>
                <H5 $mb="0">Modifier Groups</H5>
                <Icon $pointer onClick={() => setAddModifierGroupModal(true)}>
                  <PlusIcon />
                </Icon>
              </Div>
              <div>
                {modifierGroups?.map((item, i) => (
                  <AddModifierGroupComponent
                    item={item}
                    key={i}
                    edit
                    modifierGroups={modifierGroups}
                    setModifierGroups={setModifierGroups}
                  />
                ))}
              </div>
            </>
          ) : (
            <Div
              $flex
              $alignCenter
              $gap="8px"
              $pointer
              onClick={() => setAddModifierGroupModal(true)}
            >
              <Icon>
                <PlusIcon />
              </Icon>

              <H5 $m="0" $color={lightTheme.primary}>
                Add Modifier Groups
              </H5>
            </Div>
          )}
        </Div>
      </Modal>

      <AddCustomPrice
        customPriceModal={addCustomPriceModal}
        customizedPrices={customizedPrices}
        setCustomizedPrices={setCustomizedPrices}
        closeModal={() => {
          setAddCustomPriceModal(false);
        }}
      />

      <AddCustomPrice
        customPriceModal={editCustomPriceModal}
        customizedPrices={customizedPrices}
        setCustomizedPrices={setCustomizedPrices}
        closeModal={() => {
          setEditCustomPriceModal(null);
        }}
        edit
      />

      <DeleteCustomPriceModal
        deleteModal={deleteModal}
        closeModal={() => {
          setDeleteModal(null);
        }}
        setCustomizedPrices={setCustomizedPrices}
      />

      <DeleteImageModal
        deleteModal={deleteImageModal}
        closeModal={() => {
          setDeleteImageModal(false);
        }}
        setImage={setImage}
        setRemoveImage={setRemoveImage}
      />
    </>
  );
};
